/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */
import React from "react";
import { Button, Accordion, AccordionSummary, Card, Checkbox, CardContent } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import FormTextField from "../../../../../components/FormTextField/FormTextField";
import { USER_MANAGEMENT_TOOL_TIPS } from "../../../../../utility/toolTipMessages";
import { getEnableAllPermissionId } from "../../../../../utility/permissions";
import { ConfirmDialog } from "../../../../../components/ConfirmDialog/ConfirmDialog";
import { ActionButtons, ProgressOverlay } from "../../../../../components/Common";
import { EmptyPlaceholder } from "../../../../../components";

/**
 *This component displayed the role details.
 */
export const RoleDetails = ({
	selectedRole,
	setFormEdited,
	formEdited,
	pageModules,
	selectedRoleId,
	saving,
	removeRoleHandler,
	roles,
	saveRoleHandler,
	cancelHandler,
}) => {
	const [saveDisabled, setSaveDisabled] = React.useState(false);
	const [expanded, setExpanded] = React.useState({});
	const [currentRole, setCurrentRole] = React.useState(undefined);
	const [removeConfirmOpen, setRemoveConfirmOpen] = React.useState(false);

	React.useEffect(() => {
		if (
			selectedRole &&
			(!currentRole || selectedRole.roleId !== currentRole.roleId || selectedRole.newRole !== currentRole.newRole)
		) {
			setCurrentRole({ ...selectedRole, permissions: { ...selectedRole.permissions } });
			setExpanded({});
		} else if (!selectedRole) {
			setCurrentRole(undefined);
			setExpanded({});
		}
	}, [selectedRole, currentRole]);

	const onExpandAllClick = React.useCallback(() => {
		const newExpanded = {};
		pageModules.forEach((pageModule) => (newExpanded[pageModule.label] = true));
		setExpanded(newExpanded);
	}, [pageModules]);

	const onCollapseAllClick = () => {
		setExpanded({});
	};

	const updateRolePermissionHandler = React.useCallback(
		(value, permissionName) => {
			setCurrentRole((oldRole) => {
				return { ...oldRole, permissions: { ...oldRole.permissions, [permissionName]: value } };
			});
			setFormEdited(true);
		},
		[setFormEdited],
	);

	const expandModule = React.useCallback((panelName) => {
		setExpanded((oldExpanded) => ({ ...oldExpanded, [panelName]: !oldExpanded[panelName] }));
	}, []);

	const handleModuleCheckChange = React.useCallback(
		(isChecked, module) => {
			setCurrentRole((oldRole) => {
				const newRole = { ...oldRole };
				module.permissions.forEach((page) => {
					newRole.permissions[page.id] = isChecked;
				});
				return newRole;
			});
			setFormEdited(true);
		},
		[setFormEdited],
	);

	const updatePropertyHandler = React.useCallback((valueName, value) => {
		setCurrentRole((oldRole) => ({ ...oldRole, [valueName]: value }));
	}, []);

	const renderProgressBar = saving && <ProgressOverlay />;

	return (
		<CardContent>
			{renderProgressBar}
			{currentRole ? (
				<>
					<div className="d-flex align-items-center justify-content-between flex-wrap">
						<h5 className="m-0">
							<strong>{currentRole.newRole ? "Add Role" : "Edit Role"}</strong>
						</h5>
						{!currentRole.default && (
							<ActionButtons
								showCancel
								showRemove
								className="my-2 my-md-0"
								disableSave={saveDisabled}
								showSave={formEdited || saving}
								onCancel={cancelHandler}
								onRemove={() => setRemoveConfirmOpen(true)}
								onSave={() => saveRoleHandler(currentRole)}
							/>
						)}
					</div>
					<div>
						<FormTextField
							inputProps={{
								className: "w-100 w-50",
							}}
							styleObject={{ minWidth: "50%", margin: "25px 0" }}
							label={"Friendly Role Name"}
							formEdited={formEdited}
							setEdited={setFormEdited}
							startingValue={currentRole.roleName || ""}
							setConfigValue={(valueName, value) => {
								if (value) {
									updatePropertyHandler(valueName, value.trim());
								}
							}}
							valueName={"roleName"}
							disabled={!!currentRole.default}
							required
							errorCheck={(newValue) => {
								if (newValue && newValue.trim()) {
									if (newValue.length > 50) {
										setSaveDisabled(true);
										return true;
									}
									const newValueLowerCase = newValue.toLowerCase().trim();
									const duplicate = roles.find(
										(val) => val && val.roleName?.toLowerCase() === newValueLowerCase && val.roleId !== selectedRoleId,
									);
									setSaveDisabled(!!duplicate);
									return !!duplicate;
								}
								setSaveDisabled(true);
								return true;
							}}
							toolTipMessage={USER_MANAGEMENT_TOOL_TIPS.ROLE_NAME}
							errorMessage={"A role name is required, must be unique, and fewer than 50 characters."}
						/>
						<div className="d-flex justify-content-between flex-wrap py-3">
							<h5 className="my-auto mx-0">Permissions</h5>
							<div className="d-flex mt-2 mt-md-0" style={{ gap: 10 }}>
								<Button size="small" variant={"outlined"} onClick={onExpandAllClick}>
									Expand All
								</Button>
								<Button size="small" variant={"outlined"} onClick={onCollapseAllClick}>
									Collapse All
								</Button>
							</div>
						</div>
						<div className="my-3">
							{pageModules &&
								pageModules
									.sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1))
									.map((pageModule) => {
										let checked = false;
										let allChecked = true && !pageModule.disabled;
										const pageCheckBoxes = [];
										const enableAllPermission = getEnableAllPermissionId(pageModule.id);
										pageModule.permissions.forEach((page) => {
											if (currentRole.permissions[enableAllPermission] || currentRole.permissions[page.id]) {
												checked = true;
											} else {
												allChecked = false;
											}
											pageCheckBoxes.push(
												<div className="d-flex mr-3 align-items-center" key={page.id}>
													<Checkbox
														disabled={!!currentRole.default || !!pageModule.disabled}
														checked={
															!!(currentRole.permissions[enableAllPermission] || currentRole.permissions[page.id])
														}
														onChange={(event) => {
															updateRolePermissionHandler(event.target.checked, page.id);
														}}
													></Checkbox>
													<span>{page.label}</span>
												</div>,
											);
										});
										return (
											<Accordion
												className="mb-2 rounded"
												expanded={!!expanded[pageModule.label]}
												onChange={() => expandModule(pageModule.label)}
												key={pageModule.label}
											>
												<AccordionSummary
													className="text-center pl-1 rounded"
													style={{
														background: "#eeeeee",
														height: "20px",
													}}
													aria-controls={pageModule.label}
													id={pageModule.label}
													expandIcon={<ExpandMoreIcon />}
												>
													<span>
														<Checkbox
															key={pageModule.label}
															onClick={(e) => e.stopPropagation()}
															disabled={!!currentRole.default || !!pageModule.disabled}
															style={{
																color:
																	!currentRole.default && !pageModule.disabled && (checked || allChecked)
																		? "#cc1f20"
																		: undefined,
															}}
															checked={allChecked}
															indeterminate={checked && !allChecked}
															onChange={(event) => handleModuleCheckChange(event.target.checked, pageModule)}
														></Checkbox>
														{pageModule.label}
													</span>
												</AccordionSummary>
												<Card>
													<div className="d-flex flex-wrap p-3">{pageModule.permissions && pageCheckBoxes}</div>
												</Card>
											</Accordion>
										);
									})}
						</div>
					</div>
				</>
			) : (
				<EmptyPlaceholder message="To get started, select a role from the list on the left, or click the 'Add' button to create a new role" />
			)}
			<ConfirmDialog
				title={"Remove Role?"}
				message={"Are you sure you want to delete the Role?"}
				confirmOpen={removeConfirmOpen}
				onClose={async (confirmed) => {
					if (confirmed && currentRole) {
						removeRoleHandler();
					}
					setRemoveConfirmOpen(false);
				}}
			/>
		</CardContent>
	);
};
